import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { organizerPhysicalGetBatchApi } from "../../api/api";
import { MAIN_API } from "../../api/apilist";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import AuthorizedQrImage from "../../components/qr/AuthorizedQrImage";
import "./physicalTickets.css";

const asId = (v) => (v == null ? "" : String(v));
const DOT = "\u00b7";

const PrintBatch = () => {
  const { batchId } = useParams();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(null);
  const cacheBustRef = useRef(Date.now());
  const printTriggeredRef = useRef(false);
  const [loadedCount, setLoadedCount] = useState(0);

  useEffect(() => {
    document.body.classList.add("print-physical-batch");
    return () => {
      document.body.classList.remove("print-physical-batch");
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!batchId) return;
      try {
        setLoading(true);
        setErrorMsg("");
        setLoadedCount(0);
        printTriggeredRef.current = false;
        cacheBustRef.current = Date.now();
        const res = await organizerPhysicalGetBatchApi(batchId);
        setData(res?.data?.info || null);
      } catch (e) {
        setErrorMsg("No pudimos cargar el lote para imprimir.");
        handleError(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [batchId]);

  useEffect(() => {
    const total = Number(data?.tickets?.length || 0);
    if (!total) return;
    if (printTriggeredRef.current) return;
    if (loadedCount < total) return;

    printTriggeredRef.current = true;
    const t = setTimeout(() => {
      try {
        window.print();
      } catch (_) { }
    }, 150);
    return () => clearTimeout(t);
  }, [data?.tickets?.length, loadedCount]);

  const batch = data?.batch || {};
  const tickets = data?.tickets || [];

  const title = useMemo(() => {
    const base = batch?.eventName ? `${batch.eventName}` : "Entradas presenciales";
    return batch?.code ? `${base} - ${batch.code}` : base;
  }, [batch?.eventName, batch?.code]);

  if (loading) return <SpinLoading />;
  if (errorMsg) {
    return (
      <div className="organizer-page organizer-physical-print">
        <Alert variant="danger">{errorMsg}</Alert>
      </div>
    );
  }

  return (
    <div className="organizer-physical-print">
      <section className="zoco-card zoco-qr-shell">
        <div className="zoco-qr-shell__header">
          <div>
            <h2 className="zoco-card__title">
              <span>{title.split(" - ")[0]}</span>{" "}
              {batch.code ? <span className="zoco-qr-shell__code">- {batch.code}</span> : null}
            </h2>
            <p className="zoco-card__subtitle zoco-qr-shell__subtitle">
              Lote {batch.code || "-"} {DOT} {batch.ticketTypeName || "General"} {DOT} {tickets.length} entradas
            </p>
          </div>
          <button type="button" className="zoco-btn" onClick={() => window.print()}>
            Imprimir / Guardar PDF
          </button>
        </div>

        <div className="zoco-qr-grid">
          {tickets.map((t) => {
            const ticketId = asId(t.ticketId);
            const physicalSeq = t.physicalSeq != null ? String(t.physicalSeq) : "";
            const qrSrc = `${MAIN_API}/api/ticket/qr/ticket/${encodeURIComponent(ticketId)}/plain?w=900&ts=${cacheBustRef.current}`;
            return (
              <article key={ticketId} className="zoco-qr-card">
                <div className="zoco-qr-card__code">
                  {batch.code}
                  {physicalSeq ? ` ${DOT} #${physicalSeq}` : ""}
                </div>
                <div className="zoco-qr-card__title">{batch.eventName || ""}</div>
                <div className="zoco-qr-card__type">{batch.ticketTypeName || "General"}</div>
                <div className="zoco-qr-card__qr">
                  <AuthorizedQrImage
                    src={qrSrc}
                    accessToken={accessToken}
                    alt={`QR ${batch.code} ${physicalSeq}`}
                    loading="eager"
                    onLoad={() => setLoadedCount((n) => n + 1)}
                    onError={() => setLoadedCount((n) => n + 1)}
                  />
                </div>
                <div className="zoco-qr-card__caption">Escaneable con la app de scanner. Uso unico.</div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default PrintBatch;
